import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  Input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import {
  FrequencyLimitService,
  ProductFormatWithdrawalLimitsParams,
} from '../../../../woo_services.module/FrequencyLimitService';
import { ExtraProductFormatField, ProductFormatService } from '../../../../woo_services.module/ProductFormatService';
import { PublisherService } from '../../../../woo_services.module/PublisherService';
import {
  FrequencyLimit,
  ProductFormat,
  Publisher,
  SlotType,
  SlotTypeEstimates,
  wooId,
} from '../../../../woo_services.module/shared-types';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import { ProductsPageUtilityService } from '../../shared/products-page-utility-service';

@Component({
  selector: 'manage-frequency-limits',
  templateUrl: './manage-frequency-limits.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageFrequencyLimits implements OnInit {
  @Input('headerPos') parentHeader: HTMLElement;

  show = {
    estimatesHaveChanged: false,
    loading: true,
    isEditing: false,
  };

  readonly staticFields = 4;
  readonly tableHeaders = ['Namn', 'Publicist', 'Placering', 'impressions', 'Preroll', 'Midroll', 'Postroll'];

  publishers = signal<Publisher[]>([]);
  selectedPublisher = signal<Publisher | null>(null);
  productFormats = signal<ProductFormat[]>([]);
  selectedProductFormat = signal<ProductFormat | null>(null);
  frequencyLimitRows = model<FrequencyLimitRow[]>([]);
  frequencyLimits: FrequencyLimit[] = [];
  includedProductFormatSlotTypes: Record<wooId, Record<SlotType, boolean>> = {};

  translateProductFormat: (args: any) => string;

  constructor(
    private publisherService: PublisherService,
    private productFormatService: ProductFormatService,
    private frequencyLimitService: FrequencyLimitService,
    private utilityService: ProductsPageUtilityService,
    private modalService: ModalService,
    private translationService: TranslationService,
    private changeDetection: ChangeDetectorRef,
  ) {
    this.translateProductFormat = this.translationService.convertProductFormat;
  }

  public computedValues = computed(() => {
    if (this.selectedProductFormat()) {
      return this.frequencyLimitRows().filter((row) => row.productFormatId === this.selectedProductFormat().id);
    }
    return this.frequencyLimitRows();
  });

  ngOnInit(): void {
    this.frequencyLimitService
      .getAll()
      .then((limits) => (this.frequencyLimits = limits))
      .then(() =>
        this.publisherService.getPublishers().then((pubs) => {
          this.publishers.set(pubs.filter((p) => p.enabled));
        }),
      )
      .then(async () => await this.getAndBuildFrequencyLimitRows())
      .finally(() => {
        this.show = { ...this.show, loading: false };
        this.changeDetection.markForCheck();
      });
  }

  public onRatioChange(cell: any, value: number, key: string): void {
    (cell.row as any)[key] = value;
  }

  async getAndBuildFrequencyLimitRows(): Promise<void> {
    const pfs = await this.productFormatService.getProductFormats(
      ExtraProductFormatField.slotTypes,
      ExtraProductFormatField.enabled,
      ExtraProductFormatField.frequencyLimitRatios,
    );

    this.productFormats.set(pfs.filter((f) => f.enabled));
    this.includedProductFormatSlotTypes = this.utilityService.setIncludedProductFormatSlotTypes(this.productFormats());
    await this.buildFrequencyLimitRows();
  }

  setEditing(value: boolean): void {
    this.show.isEditing = value;
  }

  cancelEditing(): void {
    this.getAndBuildFrequencyLimitRows();
    this.setEditing(false);
  }

  private syncProductFormatsFromRows(): void {
    const byPfFl = new Map<string, { preroll?: number; midroll?: number; postroll?: number }>();

    for (const r of this.frequencyLimitRows()) {
      const key = `${r.productFormatId}|${r.frequencyLimitId}`;
      const cur = byPfFl.get(key) ?? {};
      if (typeof r.frequencyRatioPreroll === 'number') cur.preroll = r.frequencyRatioPreroll;
      if (typeof r.frequencyRatioMidroll === 'number') cur.midroll = r.frequencyRatioMidroll;
      if (typeof r.frequencyRatioPostroll === 'number') cur.postroll = r.frequencyRatioPostroll;
      byPfFl.set(key, cur);
    }

    this.productFormats.update((pfs) =>
      pfs.map((pf) => {
        const nextRatios = { ...(pf.frequency_limit_ratios ?? {}) };

        for (const [k, ratios] of byPfFl.entries()) {
          const [pfId, flId] = k.split('|');
          if (pfId !== pf.id) continue;

          const currentEntry = nextRatios[flId] ?? {};
          nextRatios[flId] = {
            ...currentEntry,
            ...(ratios.preroll != null ? { preroll: ratios.preroll } : {}),
            ...(ratios.midroll != null ? { midroll: ratios.midroll } : {}),
            ...(ratios.postroll != null ? { postroll: ratios.postroll } : {}),
          };
        }

        return { ...pf, frequency_limit_ratios: nextRatios };
      }),
    );
  }

  async save(): Promise<void> {
    const filterActive = !!this.selectedPublisher() || !!this.selectedProductFormat();
    const texts = ['Är du säker på att du vill spara dina ändringar?'];
    if (filterActive) texts.push('Notera att även ändringar i filtrerade rader sparas.');

    await this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts,
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      })
      .then(async (result) => {
        if (!result.confirm) return;

        const modal = this.modalService.openLoader('Sparar ändringar');
        try {
          await this.syncProductFormatsFromRows();

          const requests = this.frequencyLimits.map(async (limit) =>
            this.frequencyLimitService.updateWithdrawalLimitsByProductFormat(
              limit.id,
              this.buildProductFormatWithdrawalParams(limit.id),
            ),
          );

          await Promise.all(requests);
          this.show.estimatesHaveChanged = true;
        } catch (err) {
          this.setEditing(false);
          await this.getAndBuildFrequencyLimitRows();
          this.modalService.openError(this.translationService.convertError(err.error?.error ?? err));
        } finally {
          this.setEditing(false);
          modal.close();
          await this.getAndBuildFrequencyLimitRows();
          this.changeDetection.markForCheck();
        }
      })
      .finally(() => {
        this.modalService.closeAll();
        this.changeDetection.markForCheck();
      });
  }

  rowVisible(obj: FrequencyLimitRow): boolean {
    const publisherFiltered = this.selectedPublisher() && obj.publisherId !== this.selectedPublisher().id;
    const productFormatFiltered =
      this.selectedProductFormat() && obj.productFormatId !== this.selectedProductFormat().id;
    return !(publisherFiltered || productFormatFiltered);
  }

  getFrequencyLimit(productFormatId: wooId, frequencyLimitId: wooId): SlotTypeEstimates {
    return this.utilityService.getProductFormat(productFormatId, this.productFormats()).frequency_limit_ratios[
      frequencyLimitId
    ];
  }

  private buildProductFormatWithdrawalParams(id: wooId): ProductFormatWithdrawalLimitsParams[] {
    let limits: ProductFormatWithdrawalLimitsParams[] = [];
    return this.productFormats().flatMap(
      (format) =>
        (limits = [
          ...limits,
          {
            product_format_id: format.id,
            withdrawal_limits: Object.entries(format.frequency_limit_ratios[id]).map(([slotType, amount]) => ({
              slot_type: slotType as SlotType,
              withdrawal_limit: amount as number,
            })),
          },
        ]),
    );
  }

  private buildFrequencyLimitRows(): FrequencyLimitRow[] {
    let rows: FrequencyLimitRow[] = [];

    this.frequencyLimits.forEach((limit) => {
      this.productFormats().forEach((format) => {
        rows = [
          ...rows,
          {
            name: limit.name,
            FrequencyLimitId: limit.id,
            publisherName: this.utilityService.getPublisherName(format.publisher_id, this.publishers()),
            publisherId: format.publisher_id,
            productFormat: format.name,
            productFormatId: format.id,
            frequencyLimitId: limit.id,
            impressions: limit.impressions,
            frequencyRatioPreroll: format.frequency_limit_ratios[limit.id].preroll,
            frequencyRatioMidroll: format.frequency_limit_ratios[limit.id].midroll,
            frequencyRatioPostroll: format.frequency_limit_ratios[limit.id].postroll,
          },
        ];
      });
    });

    this.frequencyLimitRows.set(rows);
    return this.frequencyLimitRows();
  }
}

interface FrequencyLimitRow {
  name: string;
  FrequencyLimitId: wooId;
  publisherName: string;
  publisherId?: wooId;
  productFormat: string;
  productFormatId?: wooId;
  frequencyLimitId: wooId;
  impressions: number;
  frequencyRatioPreroll?: number;
  frequencyRatioMidroll?: number;
  frequencyRatioPostroll?: number;
}
